'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { HTMLAttributes } from 'react';
import { EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { ProductDataSheetsDownloadsList } from '../ProductDataSheetsDownloadsList/ProductDataSheetsDownloadsList';
import {
  EfficiencyFlagList,
  type EfficiencyFlagListProps,
} from '../EfficiencyFlagList/EfficiencyFlagList';

/* GraphQL */ `
  fragment EfficiencyFlagsAndDataSheetsDownloadCardFragment on Product {
    ...EfficiencyFlagListFragment
    ...ProductDataSheetsDownloadsListFragment
  }
`;

export type EfficiencyFlagsAndDataSheetsDownloadCardProps = HTMLAttributes<HTMLDivElement> & {
  /** List with efficiencyFlag properties to simply render component. */
  data: FragmentType<typeof EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc>;
} & Pick<EfficiencyFlagListProps, 'horizontal' | 'size'>;

/**
 * EfficiencyFlagsAndDataSheetsDownloadCard is a component which displays a list of the `EfficiencyFlags` and product data sheet downloads.
 * It has a special layout variant used in the detail view.
 * */
export const EfficiencyFlagsAndDataSheetsDownloadCard = ({
  data,
  horizontal,
  size,
  ...props
}: EfficiencyFlagsAndDataSheetsDownloadCardProps) => {
  const unmaskedData = getFragmentData(
    EfficiencyFlagsAndDataSheetsDownloadCardFragmentFragmentDoc,
    data,
  );

  const productDataSheetsDownloadsComponent = ProductDataSheetsDownloadsList({
    data: unmaskedData,
  });

  return (
    <div {...props}>
      <Card className="border-color-grey rounded-[4px] border shadow-none">
        <CardContent className="pt-1 pb-1">
          <EfficiencyFlagList data={unmaskedData} horizontal={horizontal} size={size} />
        </CardContent>
        {productDataSheetsDownloadsComponent !== null && (
          <>
            <Divider />
            <CardContent className="pt-1 pb-1">{productDataSheetsDownloadsComponent}</CardContent>
          </>
        )}
      </Card>
    </div>
  );
};
